import { type Icon, LockKey } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { Localized } from "@plannello/shared";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  STATUS_CATEGORIES,
  type StatusCategory,
  StatusIcon,
  statusCategoryLabel,
} from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { Page } from "@/components/shell/Page";
import { buttonStyles, EmptyState, type Option } from "@/components/ui";
import { usePermissions } from "@/components/views/api";
import { cx } from "@/lib/cx";
import { labelSwatch, loc, typeTint } from "@/lib/format";

/** Whether the person may change item types and workflows (schema.manage). Reads need nothing. */
export function useCanManageSchema(): { pending: boolean; can: boolean } {
  const perms = usePermissions();
  return { pending: perms.isPending, can: perms.instance("schema.manage") };
}

/** Shown instead of an editor when schema.manage is missing: says what is needed and who can help. */
export function SchemaDenied({ title }: { title: string }) {
  return (
    <Page title={title} icon={LockKey}>
      <EmptyState
        icon={LockKey}
        title={m.adm_section_denied_title()}
        description={m.adm_section_denied_body({ permission: "schema.manage" })}
        action={
          <Link to="/admin" className={buttonStyles({ variant: "secondary" })}>
            {m.adm_title()}
          </Link>
        }
      />
    </Page>
  );
}

/** The other language of a name, for the quiet second line ("Aufgabe" under "Task"). */
export function otherName(name: Localized, current: string): string | null {
  const n = name as { en?: string; de?: string };
  const other = [n.en, n.de].find((v) => v && v !== current);
  return other ?? null;
}

// ---------------------------------------------------------------------------------------------------
// Levels

export const LEVELS = [3, 2, 1, 0] as const;

export const levelName = (level: number): string =>
  ({ 3: m.sch_level_3(), 2: m.sch_level_2(), 1: m.sch_level_1(), 0: m.sch_level_0() })[level] ?? String(level);

export const levelBlurb = (level: number): string =>
  ({ 3: m.sch_level_3_hint(), 2: m.sch_level_2_hint(), 1: m.sch_level_1_hint(), 0: m.sch_level_0_hint() })[level] ?? "";

/** Four steps; a type at level N lights N + 1 of them, so the shape (not the colour) tells the level. */
export function LevelBars({ level, size = 12 }: { level: number; size?: number }) {
  return (
    <span aria-hidden="true" className="inline-flex shrink-0 items-end gap-px" style={{ height: size }}>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={cx("w-[3px] rounded-[1px]", i <= level ? "bg-fg-muted" : "bg-line-strong")}
          style={{ height: Math.round(((i + 2) / 5) * size) }}
        />
      ))}
    </span>
  );
}

/** Hierarchy chip: "Epic 2" with the level bars. */
export function LevelChip({ level, className }: { level: number; className?: string }) {
  return (
    <span
      className={cx(
        "inline-flex h-5 shrink-0 items-center gap-1.5 rounded-mark border border-line bg-surface px-1.5 text-xs font-medium whitespace-nowrap text-fg-muted",
        className,
      )}
    >
      <LevelBars level={level} />
      {levelName(level)}
      <span className="font-mono text-fg-subtle tnum">{level}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------
// Types

/** The type's glyph in a tile tinted with its colour: the same shape the item views use everywhere. */
export function TypeTile({
  icon,
  color,
  size = "md",
  className,
}: {
  icon: string;
  color: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const box = size === "lg" ? "size-10" : size === "md" ? "size-8" : "size-6";
  return (
    <span
      aria-hidden="true"
      className={cx(
        "flex shrink-0 items-center justify-center rounded-control border border-line bg-surface shadow-[0_1px_2px_var(--shadow-soft)]",
        box,
        className,
      )}
      style={{ color: typeTint(color) ?? labelSwatch(color) }}
    >
      <TypeIcon name={icon} decorative size={size === "lg" ? 20 : size === "md" ? 16 : 14} className="text-current" />
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------
// Workflows

export const CATEGORY_HINT: Record<StatusCategory, () => string> = {
  backlog: () => m.sch_cat_backlog_hint(),
  unstarted: () => m.sch_cat_unstarted_hint(),
  started: () => m.sch_cat_started_hint(),
  completed: () => m.sch_cat_completed_hint(),
  canceled: () => m.sch_cat_canceled_hint(),
};

export type PipelineStatus = { id: string; name: Localized; category: StatusCategory };

/** Statuses grouped by category in pipeline order, empty categories dropped. */
export function groupByCategory<T extends { category: StatusCategory }>(statuses: readonly T[]) {
  return STATUS_CATEGORIES.map((category) => ({ category, statuses: statuses.filter((s) => s.category === category) }));
}

/**
 * A workflow at a glance: its statuses as small chips, grouped by category from Backlog to Canceled
 * with a wider gap between groups. The glyph carries the category, the label is the status name.
 */
export function WorkflowPipeline({
  statuses,
  label,
  className,
}: {
  statuses: readonly PipelineStatus[];
  label: string;
  className?: string;
}) {
  const groups = groupByCategory(statuses).filter((g) => g.statuses.length > 0);
  return (
    <ul aria-label={label} className={cx("m-0 flex list-none flex-wrap items-center gap-x-3 gap-y-1.5 p-0", className)}>
      {groups.map((g) => (
        <li key={g.category} className="flex items-center">
          <ul
            aria-label={statusCategoryLabel(g.category)}
            className="m-0 flex list-none flex-wrap items-center gap-1 p-0"
          >
            {g.statuses.map((s) => (
              <li
                key={s.id}
                className="inline-flex h-5 max-w-36 items-center gap-1 rounded-mark border border-line bg-surface px-1.5 text-xs text-fg-muted"
              >
                <StatusIcon category={s.category} size={12} decorative />
                <span className="truncate-title">{loc(s.name)}</span>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Small section heading used by both editors. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx("text-xs font-medium text-fg-subtle", className)}>{children}</p>;
}

// Select options that show the category glyph (the Select renders `icon` as a component).
const STATUS_ICONS = Object.fromEntries(
  STATUS_CATEGORIES.map((category) => [
    category,
    ((props: { size?: number }) => (
      <StatusIcon category={category} size={props.size ?? 16} decorative />
    )) as unknown as Icon,
  ]),
) as Record<StatusCategory, Icon>;

/** A status as a Select option: glyph, name, and the category in the spoken text. */
export function statusOption(s: PipelineStatus): Option {
  const name = loc(s.name);
  return {
    id: s.id,
    label: name,
    icon: STATUS_ICONS[s.category],
    textValue: `${name}, ${statusCategoryLabel(s.category)}`,
  };
}
